import React, { useState } from 'react';

export default function DeployAssessmentModal({ isOpen, onClose, onDeploy, assessmentType, isDeployed }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    let deployAt = null;
    if (date && time) {
      deployAt = new Date(`${date}T${time}`);
      if (isNaN(deployAt.getTime())) {
        setError('Invalid date or time.');
        setIsSubmitting(false);
        return;
      }
      deployAt = deployAt.toISOString();
    } else if (date || time) {
      setError('Please provide both date and time, or leave both blank for immediate deployment.');
      setIsSubmitting(false);
      return;
    }
    try {
      await onDeploy(deployAt);
      onClose();
    } catch (err) {
      setError('Failed to deploy assessment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isScheduled = date && time;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Deploy {assessmentType} Assessment</h3>
          <button onClick={onClose} className="close-button">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          
          {/* Assessment Access Information */}
          <div style={{ 
            background: '#f0f9ff', 
            border: '1px solid #bae6fd', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginBottom: '1.5rem' 
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#0369a1', fontSize: '0.9rem' }}>
              📧 Assessment Access Information
            </h4>
            {!isScheduled ? (
              <div style={{ fontSize: '0.85rem', color: '#0c4a6e' }}>
                <p style={{ margin: '0 0 0.5rem 0' }}>
                  <strong>Immediate Deployment:</strong> The assessment will be available right away.
                </p>
                <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                  <li style={{ marginBottom: '0.25rem' }}>
                    <strong>Stakeholders (Internal/External):</strong> Will receive invitation emails with assessment links
                  </li>
                  <li>
                    <strong>Employees:</strong> Can access via "My Assessments" portal (no email sent)
                  </li>
                </ul>
              </div>
            ) : (
              <div style={{ fontSize: '0.85rem', color: '#0c4a6e' }}>
                <p style={{ margin: '0 0 0.5rem 0' }}>
                  <strong>Scheduled Deployment:</strong> The assessment will be available on <strong>{new Date(`${date}T${time}`).toLocaleString()}</strong>.
                </p>
                <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                  <li style={{ marginBottom: '0.25rem' }}>
                    <strong>Stakeholders (Internal/External):</strong> Will receive invitation emails on the scheduled date
                  </li>
                  <li>
                    <strong>Employees:</strong> Will see the assessment in "My Assessments" portal on the scheduled date
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div className="form-group">
            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
              Schedule Deployment (optional):
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                disabled={isSubmitting}
                style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
              <input
                type="time"
                value={time}
                onChange={e => setTime(e.target.value)}
                disabled={isSubmitting}
                style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
            </div>
            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Leave blank to deploy and send emails immediately.
            </div>
          </div>

          {/* Stakeholder Assignment Reminder */}
          <div style={{ 
            background: '#fffbeb', 
            border: '1px solid #fed7aa', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginTop: '1rem' 
          }}>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#92400e' }}>
              <strong>💡 Remember:</strong> Make sure to assign stakeholders and employees to this assessment before deploying. 
              Only assigned individuals will have access to the assessment.
            </p>
          </div>

          {error && (
            <div className="alert alert-danger" style={{ 
              background: '#fef2f2', 
              border: '1px solid #fecaca', 
              color: '#991b1b', 
              padding: '0.75rem', 
              borderRadius: '6px', 
              marginTop: '1rem' 
            }}>
              {error}
            </div>
          )}
          
          <div className="modal-footer" style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '0.75rem', 
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button 
              type="button" 
              className="button btn-secondary" 
              onClick={onClose} 
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                border: '1px solid #d1d5db',
                background: 'white',
                color: '#374151',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className={`button btn-primary${isDeployed ? ' button-disabled' : ''}`} 
              disabled={isSubmitting || isDeployed}
              style={{
                padding: '0.75rem 1.5rem',
                background: isDeployed ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isDeployed ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? 'Deploying...' : (isScheduled ? 'Schedule Deployment' : 'Deploy Now')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 